import { Button, Card, TableColHeading } from "components";
import React from "react";
import { formatISODate, formatNumber } from "utils/formatting";
import { COLOR, FONT_SIZE } from "constants/styles";
import Skeleton from "react-loading-skeleton";
import { EnhancedSubscription } from "types/Plans";
import { CardProps } from "components/UI/Card";
import { SubscriptionAddonSchema } from "types/addons";
import { SUBSCRIPTION_ADDONS } from "constants/addons";
import Link from "next/link";
import { routes } from "utils/routes";
import { PPUBalanceInfo } from "services/ppu/types";

interface Props extends Omit<CardProps, "children"> {
  subscription: EnhancedSubscription | undefined;
  ppu: PPUBalanceInfo | undefined | null;
  addons?: SubscriptionAddonSchema[] | undefined;
  showPPUActions?: boolean;
}

const SubscriptionInfo = ({
  subscription,
  addons,
  ppu,
  showPPUActions,
  ...rest
}: Props) => {
  const subscriptionBillingCycle =
    subscription?.currentPlan.stripePrice.billingCycle;

  return (
    <>
      <Card
        title="Your Subscription"
        description="Your currently active subscription and purchased add-ons"
        {...rest}
      >
        <div className="current-plan" data-testid="current-plan-info">
          {!subscription && !ppu && <Skeleton width={200} height={20} />}

          {ppu?.active && (
            <div className="ppu">
              <div className="plan-name-wrapper">
                <span className="plan-name" data-testid="current-plan-name">
                  Pay As You Go
                </span>
                {showPPUActions && (
                  <div className="plan-action-wrapper">
                    <Link href={routes.ppuNewOrder}>
                      <Button
                        uppercase
                        variant="outline-primary"
                        size="xs"
                        data-testid="buy-resources-btn"
                      >
                        Buy Resources
                      </Button>
                    </Link>
                    <Link href={routes.dashboardSubscriptionPlans}>
                      <Button
                        uppercase
                        variant="outline-primary"
                        size="xs"
                        data-testid="change-plan-btn"
                        style={{ marginLeft: "8px" }}
                      >
                        Change Plan
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}

          {ppu && !ppu.active && subscription && (
            <div className="plan-name-wrapper">
              <span className="plan-name" data-testid="current-plan-name">
                {subscription.currentPlan.name}
              </span>
              {/* <div className="line"></div> */}
              <span>&nbsp;(</span>
              <span>
                <span>Billed&nbsp;</span>
                <span>
                  {subscriptionBillingCycle === "year" ? "yearly" : "monthly"}
                </span>
              </span>
              <span>)</span>
              <div className="plan-action-wrapper">
                <Link href={routes.dashboardSubscriptionPlans}>
                  <Button
                    uppercase
                    variant="outline-primary"
                    size="xs"
                    data-testid="change-plan-btn"
                  >
                    Change Plan
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="rate-limit">
          <div className="subtitle">Rate Limit</div>
          {(!subscription || !ppu) && <Skeleton width={240} height={20} />}

          {ppu && ppu.active && (
            <>
              {formatNumber(ppu.balance.limit_per_second)}{" "}
              <span>requests/s</span>
              <span>&nbsp;(</span>
              {ppu.balance.limit_burst}
              <span> requests in burst)</span>
            </>
          )}

          {subscription && ppu && !ppu.active && (
            <>
              {formatNumber(subscription.currentPlan.metadata.limitPerSecond)}{" "}
              <span>requests/s</span>
              <span>&nbsp;(</span>
              {subscription.currentPlan.metadata.limitBurst}
              <span> requests in burst)</span>
            </>
          )}
        </div>

        {addons && addons.length > 0 && (
          <div className="addons">
            <>
              <TableColHeading>Addon</TableColHeading>
              <TableColHeading>Description</TableColHeading>
              <TableColHeading>Expires at</TableColHeading>
              {addons.map(addon => {
                const predefinedAddon = SUBSCRIPTION_ADDONS.find(
                  a => a.type === addon.type
                );
                return (
                  <>
                    <div className="addon-type" data-testid="addon-type">
                      {predefinedAddon?.label ?? addon.type}
                    </div>
                    <div className="addon-description">
                      {predefinedAddon?.description ?? ""}
                    </div>
                    <div className="addon-expires-at">
                      {addon.expires_at
                        ? formatISODate(addon.expires_at)
                        : undefined}
                    </div>
                  </>
                );
              })}
            </>
          </div>
        )}
      </Card>

      <style jsx>{`
        .ppu {
          display: flex;
          flex-direction: column;
        }

        /* .subtitle-balance {
          margin-top: 24px;
        } */

        .plan-name-wrapper {
          display: flex;
          align-items: center;
        }

        .plan-action-wrapper {
          margin-left: 16px;
        }

        .plan-name {
          color: ${COLOR.PRIMARY};
          display: flex;
          align-items: center;
          justify-content: flex-start;
          font-weight: 500;
          font-size: ${FONT_SIZE.LG};
        }

        .current-plan {
          display: flex;
          align-items: baseline;
          font-size: ${FONT_SIZE.PRIMARY};
          /* font-weight: 500; */
          color: ${COLOR.TEXT_PRIMARY};
        }

        .rate-limit {
          font-size: ${FONT_SIZE.PRIMARY};
          /* font-weight: 500; */
          color: ${COLOR.TEXT_PRIMARY};
          margin-top: 24px;
        }

        .subtitle {
          font-weight: 500;
          font-size: ${FONT_SIZE.SECONDARY};
          letter-spacing: 0.5px;
          text-transform: uppercase;
          color: ${COLOR.TEXT_PRIMARY};
          margin-bottom: 8px;
        }

        .addons {
          display: grid;
          grid-template-columns: 1fr 2fr 1fr;
          gap: 8px 24px;
          margin-top: 24px;
        }
      `}</style>
    </>
  );
};

export default SubscriptionInfo;
